import { Button, ButtonSpinner, H2 } from "@sellar-io/components";
import { useShallow } from "zustand/react/shallow";
import {
  InputLabel,
  InputContainer,
  InputSelect,
  InputOption,
} from "@sellar-io/components";
import {
  BuyerCreationFlowStep,
  useBuyerCreationFlowStore,
} from "./BuyerCreationFlowStore";
import { Icon } from "../../../../../components";
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import { useEffect, useState } from "react";
import { useDebounce } from "use-debounce";
import { useSignupContext } from "../../../../../hooks/useSignupContext.shared";
import { TwoCompanySearchResponseType } from "@sellar-io/api-v2/types";
import { useTwoCompanySearch } from "../../../../../hooks/api/v2/two/useTwoCompanySearch";
import { useFloatingLayer } from "@sellar-io/components";

export const BuyerFindCompanyStep = ({
  isNewVenue,
}: {
  isNewVenue: boolean;
}) => {
  const { setStep } = useBuyerCreationFlowStore(
    useShallow((state) => ({
      setStep: state.setStep,
      postcode: state.postcode,
    })),
  );

  const { company, setCompany } = useSignupContext();
  const [query, setQuery] = useState("");
  const [country] = useState("GB");
  const [searchQuery] = useDebounce(query, 500);

  const twoCompanySearchQuery = useTwoCompanySearch(
    {
      organization_number: searchQuery,
    },
    {
      enabled: !!searchQuery && searchQuery.length > 2,
    },
  );

  // only include companies with company number
  const companies = (twoCompanySearchQuery.data ?? []).filter(
    (company) => company.companyNumber,
  );

  const noResultsFound =
    searchQuery.length > 2 &&
    !companies.length &&
    !twoCompanySearchQuery.isLoading;

  useEffect(() => {
    // skip limited company check if buyer created via dashboard
    if (isNewVenue) {
      setStep(BuyerCreationFlowStep.BuyerPostcodeStep);
    }
  }, [isNewVenue, setStep]);

  const { popover } = useFloatingLayer();

  return (
    <div className={"flex h-full flex-col gap-4"}>
      <H2 className={"my-4 mb-8 text-center"}>Find your company</H2>
      <p className={"text-center"}>Please enter your limited company details</p>
      <>
        <div className="flex w-full flex-col gap-2 text-left">
          <InputLabel>Country</InputLabel>
          <InputContainer className="w-full">
            <InputSelect value={country} className="w-full" disabled={true}>
              <InputOption value={"GB"}>United Kingdom</InputOption>
            </InputSelect>
          </InputContainer>
        </div>
        <div className="flex w-full flex-col gap-2 text-left">
          <InputLabel>Company</InputLabel>
          <InputContainer className="relative">
            <Icon icon="MAGNIFY" className="absolute" />
            <Combobox
              value={company}
              onChange={setCompany}
              onClose={() => setQuery("")}
            >
              <ComboboxInput
                displayValue={(
                  company: TwoCompanySearchResponseType[number] | null,
                ) => company?.companyName ?? ""}
                onChange={(event) => setQuery(event.target.value)}
                className={"w-full border-none pl-7 outline-hidden"}
                placeholder="Search by company number"
              />
              <ComboboxOptions
                anchor="bottom"
                className={`relative ${popover} mt-4 h-[300px] w-[418px] bg-white empty:invisible`}
              >
                {companies.map((company) => (
                  <ComboboxOption
                    key={company.companyName}
                    value={company}
                    className="flex w-full items-center gap-2 border-b border-b-zinc-300 p-1 data-focus:bg-zinc-100"
                  >
                    <Icon icon="MAP_MARKER" className="flex-none" />
                    <span className="flex-none font-semibold">
                      {company.companyName}
                    </span>
                  </ComboboxOption>
                ))}
              </ComboboxOptions>
            </Combobox>
            {twoCompanySearchQuery.isFetching && (
              <ButtonSpinner className="absolute right-4 text-black" />
            )}
          </InputContainer>
        </div>

        {noResultsFound && (
          <div className="mb-4 text-zinc-800">
            <span className="font-semibold">No results found. </span>No company
            found associated with the company number, please check and try
            again.
          </div>
        )}

        <div className="mt-auto flex w-full flex-col gap-2">
          <Button
            disabled={!company}
            onClick={() => {
              setStep(BuyerCreationFlowStep.BuyerPostcodeStep);
            }}
            className="w-full"
            type="submit"
          >
            Continue
          </Button>
          <Button
            color="transparent"
            className="w-full"
            onClick={() => setStep(BuyerCreationFlowStep.BuyerPostcodeStep)}
          >
            I don't have a limited company
          </Button>
        </div>
      </>
    </div>
  );
};
